/** Small presentation helpers shared across pages. */

/**
 * Per-file upload ceiling surfaced in the UI and pre-checked before upload.
 * NOTE: this mirrors the backend's `spring.servlet.multipart.max-file-size`
 * (10 MB). It is duplicated here only for a fast client-side hint/guard; the
 * backend remains the source of truth. If the limit ever needs to be dynamic,
 * it should be exposed by a backend config/limits endpoint.
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatBytes(bytes?: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

export function initials(name?: string | null, fallback = '?'): string {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const text = parts.map((p) => p[0]).join('');
  return text ? text.toUpperCase() : fallback;
}

/** Pretty-print a JSON string; returns the original on parse failure. */
export function prettyJson(raw?: string | null): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

/** Map a status-ish string to a badge variant class. */
export function statusTone(status?: string | null): 'ok' | 'warn' | 'danger' | 'neutral' | 'accent' {
  const s = (status ?? '').toUpperCase();
  if (['ACTIVE', 'INDEXED', 'COMPLETED', 'DONE', 'FINAL', 'UP', 'SUCCEEDED', 'READY', 'OK', 'COMPLETE'].includes(s)) return 'ok';
  if (['PENDING', 'RUNNING', 'PROCESSING', 'DRAFT', 'QUEUED', 'PARTIAL',
       'GENERATING', 'DRAFTING', 'SCANNING', 'ANALYZING', 'PLANNING', 'RETRIEVING', 'SYNTHESIZING'].includes(s)) return 'warn';
  if (['DISABLED', 'FAILED', 'ERROR', 'DOWN', 'REVOKED', 'AUDIT_FAILED', 'FAILED_PERMANENT'].includes(s)) return 'danger';
  return 'neutral';
}
